import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  SalesProductCapability,
  SalesProductCapabilityBillingUnit,
  SalesProductCapabilityCategory,
  SalesProductCapabilityUpsertPayload,
  createSalesCapability,
  deleteSalesCapability,
  listSalesCapabilities,
  updateSalesCapability,
} from '../../services/salesCapabilitiesApi';
import { nextSortOrder } from '../../utils/sortOrderList';

const CATEGORY_OPTIONS: { value: SalesProductCapabilityCategory; label: string }[] = [
  { value: 'Module', label: 'Módulo' },
  { value: 'AddOn', label: 'Add-on' },
  { value: 'Service', label: 'Serviço' },
  { value: 'Limit', label: 'Limite' },
];

const BILLING_UNIT_OPTIONS: { value: SalesProductCapabilityBillingUnit; label: string }[] = [
  { value: 'Flat', label: 'Valor fixo' },
  { value: 'PerSeat', label: 'Por assento' },
  { value: 'PerUnit', label: 'Por unidade' },
  { value: 'Usage', label: 'Por uso' },
];

type CapabilityFormState = Omit<SalesProductCapabilityUpsertPayload, 'sortOrder'>;

const emptyForm: CapabilityFormState = {
  name: '',
  description: '',
  category: 'Module',
  billingUnit: 'Flat',
  isActive: true,
};

function categoryLabel(value: string): string {
  return CATEGORY_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

function billingUnitLabel(value: string): string {
  return BILLING_UNIT_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

function buildCapabilityPayload(
  capability: SalesProductCapability,
  sortOrder: number,
): SalesProductCapabilityUpsertPayload {
  return {
    name: capability.name,
    description: capability.description,
    category: capability.category,
    billingUnit: capability.billingUnit,
    isActive: capability.isActive,
    sortOrder,
  };
}

export function CapabilitiesPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.capabilities');
  const [items, setItems] = useState<SalesProductCapability[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(500);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CapabilityFormState>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listSalesCapabilities({ page: targetPage, pageSize });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar capabilities.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updateSalesCapability(item.id, buildCapabilityPayload(item, sortOrder));
    },
    onReordered: () => loadData(page),
  });

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(capability: SalesProductCapability): void {
    expandForm();
    setEditingId(capability.id);
    setForm({
      name: capability.name,
      description: capability.description,
      category: capability.category,
      billingUnit: capability.billingUnit,
      isActive: capability.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: SalesProductCapabilityUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? nextSortOrder(items),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateSalesCapability(editingId, payload);
        toast.success('Capability atualizada.');
        resetForm();
      } else {
        await createSalesCapability(payload);
        toast.success('Capability criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar capability.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(capability: SalesProductCapability): Promise<void> {
    if (!canWrite) return;

    const accepted = await confirm({
      title: 'Excluir capability',
      message: `Deseja excluir "${capability.name}"? Produtos vinculados perderão este vínculo.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteSalesCapability(capability.id);
      toast.success('Capability excluída.');
      if (editingId === capability.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir capability.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Capabilities de produto"
        description="Catálogo de funcionalidades reutilizáveis — base para composição de ofertas e precificação futura."
        actions={
          <Link to="/sales/products" className="erp-link-back">
            ← Voltar para produtos
          </Link>
        }
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar capability' : 'Nova capability'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'capability selecionada'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar uma nova capability"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
          <div className="erp-form-grid">
            <label className="erp-label">
              Nome
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <label className="erp-label">
              Categoria
              <select
                className="erp-input"
                value={form.category ?? 'Module'}
                onChange={(event) =>
                  setForm({ ...form, category: event.target.value as SalesProductCapabilityCategory })
                }
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Unidade de cobrança
              <select
                className="erp-input"
                value={form.billingUnit ?? 'Flat'}
                onChange={(event) =>
                  setForm({ ...form, billingUnit: event.target.value as SalesProductCapabilityBillingUnit })
                }
              >
                {BILLING_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="erp-label mt-3 block">
            Descrição
            <textarea
              className="erp-input mt-1 min-h-[80px]"
              value={form.description ?? ''}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </label>
          <label className="erp-label-inline mt-3">
            <input
              type="checkbox"
              checked={form.isActive ?? true}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            Ativa
          </label>
          <FormActionBar
            onCancel={() => collapseForm(resetForm)}
            cancelLabel="Cancelar"
            className="erp-btn-actions mt-4"
          >
            <button type="submit" disabled={saving} className="erp-btn-primary">
              {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
            </button>
          </FormActionBar>
        </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  {canWrite ? <th className="w-10" /> : null}
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>Cobrança</th>
                  <th>Status</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 6 : 4} className="text-center text-secondary-500">
                      Nenhuma capability cadastrada.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((capability, index) => (
                    <tr key={capability.id}>
                      {canWrite ? (
                        <td>
                          <SortOrderControls
                            canMoveUp={index > 0}
                            canMoveDown={index < sortedItems.length - 1}
                            disabled={moving}
                            onMoveUp={() => void move(index, 'up')}
                            onMoveDown={() => void move(index, 'down')}
                          />
                        </td>
                      ) : null}
                      <td>{capability.name}</td>
                      <td>{categoryLabel(capability.category)}</td>
                      <td>{billingUnitLabel(capability.billingUnit)}</td>
                      <td>
                        <ActiveStatusBadge active={capability.isActive} feminine />
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(capability)} label="Editar" />
                            <DeleteIconButton onClick={() => void handleDelete(capability)} label="Excluir" />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > pageSize ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
